import React, { useState } from "react";
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	Paper,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Snackbar,
	Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddUserModal from "./AddUserModal";
import { roleMappings } from "../../utils/constants";
import { createContact, deleteContact, updateContact } from "../../service/adminService";
import { useTranslation } from "react-i18next";
import styles from "./ContactsInfomation.module.css"

const ContactsInformation = ({ contacts, permissions }) => {
	const { t } = useTranslation();
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [contactToDelete, setContactToDelete] = useState(null);
	const [editMode, setEditMode] = useState(false);
	const [selectedContact, setSelectedContact] = useState(null);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState("success");

	const handleOpenModal = () => {
		setEditMode(false);
		setSelectedContact(null);
		setOpenModal(true);
	};

	const handleOpenEditModal = (contact, index) => {
		setEditMode(true);
		setSelectedContact({ ...contact, index });
		setOpenModal(true);
	};

	const handleCloseModal = () => setOpenModal(false);

	const handleOpenDeleteDialog = (contact) => {
		setContactToDelete(contact);
		setOpenDeleteDialog(true);
	};

	const handleCloseDeleteDialog = () => {
		setContactToDelete(null);
		setOpenDeleteDialog(false);
	};

	const handleConfirmDelete = async () => {
		try {
			await deleteContact(contactToDelete);
			handleCloseDeleteDialog();
			setSnackbarMessage(t("Company Contact User Deletion"));
			setSnackbarSeverity("success");
		} catch (error) {
			setSnackbarMessage(t("Failed to delete contact"));
			setSnackbarSeverity("error");
		}
		setSnackbarOpen(true);
	};

	const addUser = async (newUser) => {
		try {
			await createContact(newUser);
			handleCloseModal();
			setSnackbarMessage(t("Company Contact User Create"));
			setSnackbarSeverity("success");
		} catch (error) {
			setSnackbarMessage(t("Failed to create contact"));
			setSnackbarSeverity("error");
		}
		setSnackbarOpen(true);
	};

	const editUser = async (updatedUser) => {
		try {
			await updateContact(updatedUser);
			handleCloseModal();
			setSnackbarMessage(t("Company Contact User Update"));
			setSnackbarSeverity("success");
		} catch (error) {
			setSnackbarMessage(t("Failed to update contact"));
			setSnackbarSeverity("error");
		}
		setSnackbarOpen(true);
	};

	const handleSnackbarClose = () => {
		setSnackbarOpen(false);
		setSnackbarMessage("");
	};

	return (
		<>
			<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px", alignItems: "center" }}>
				<h3 style={{ fontSize: "20px", color: "slategray" }}>{t("Company Contacts")}</h3>
				{permissions?.add && (
					<Button variant='contained' color='primary' onClick={handleOpenModal}>
						{t("Add User")}
					</Button>
				)}
			</div>
			<TableContainer component={Paper} style={{ maxHeight: "400px", overflowY: "auto" }}>
				<Table stickyHeader size='small'>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("First Name")}</TableCell>
							<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("Last Name")}</TableCell>
							<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("Email")}</TableCell>
							<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("Role")}</TableCell>
							{(permissions?.edit || permissions?.delete) && (
								<TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("Action")}</TableCell>
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{contacts.map((contact, index) => (
							<TableRow key={index}>
								<TableCell sx={{ textAlign: "center" }}>{contact?.first_name}</TableCell>
								<TableCell sx={{ textAlign: "center" }}>{contact?.last_name}</TableCell>
								<TableCell sx={{ textAlign: "center" }}>{contact?.email}</TableCell>
								<TableCell sx={{ textAlign: "center" }}>{roleMappings[contact?.sap_function_type_code]}</TableCell>
								{(permissions?.edit || permissions?.delete) && (
									<TableCell sx={{ textAlign: "center" }}>
										{permissions?.delete && (
											<IconButton onClick={() => handleOpenDeleteDialog(contact)} aria-label={t("Delete Account")}>
												<DeleteIcon />
											</IconButton>
										)}
										{permissions?.edit && (
											<IconButton onClick={() => handleOpenEditModal(contact, index)} aria-label={t("Edit Account")}>
												<EditIcon />
											</IconButton>
										)}
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<AddUserModal
				open={openModal}
				onClose={handleCloseModal}
				onSubmit={editMode ? editUser : addUser}
				initialValues={editMode ? selectedContact : undefined}
			/>

			<Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
				<DialogTitle>{t("Confirm Deletion")}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t("Are you sure you want to delete this contact?")}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDeleteDialog} color='primary'>
						{t("Cancel")}
					</Button>
					<Button onClick={handleConfirmDelete} color='secondary'>
						{t("Delete")}
					</Button>
				</DialogActions>
			</Dialog>
			<div className={`backdrop ${snackbarOpen ? "backdrop--show" : ""}`}></div>
			<Snackbar open={snackbarOpen} autoHideDuration={30000} onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "top", horizontal: "center" }} className={styles.snackbarCenter}>
				<Alert onClose={handleSnackbarClose} severity={snackbarSeverity}
					sx={{ width: "100%", fontSize: "16px", textAlign: "center" }}>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ContactsInformation;
